import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFilter, FiDownload, FiSearch, FiX, FiUser, FiFileText, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { Select, Input, Button, Pagination, Modal, message, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { apiFetch, apiUrl } from '../api';
import { getVisitStatusBadgeClass, getVisitStatusLabel, matchesVisitStatusFilter } from '../utils/visitStatus';
import FilterModal from '../components/FilterModal';
import VisitDetailsModal from '../components/VisitDetailsModal';
import { getLocalDateString } from '../utils/date';
import { isEmployeeRole } from '../utils/companyRoles';

const formatLogTime = (value) => (
  value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '---'
);

const VisitorLogs = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{"role": "admin"}');
  const userRole = user.role?.toLowerCase() || '';
  const canCheckInOut = userRole === 'receptionist';
  const isEmployee = isEmployeeRole(userRole);

  const [visitorQuery, setVisitorQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalDocs, setTotalDocs] = useState(0);
  const [previewPhoto, setPreviewPhoto] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [selectedVisitDetails, setSelectedVisitDetails] = useState(null);
  const [sortBy, setSortBy] = useState('checkInTime');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const resp = await apiFetch('/api/employees?all=true');
        const data = await resp.json();
        if (data.success) {
          setEmployees(data.data);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (isEmployee && employees.length > 0 && !activeFilters.hostId) {
      const myEmp = employees.find(e => e.email === user.email);
      if (myEmp) {
        setActiveFilters(prev => ({ ...prev, hostId: myEmp._id }));
      }
    }
  }, [employees, isEmployee, user.email, activeFilters.hostId]);

  const openPhotoPreview = (photo, name) => {
    if (!photo) return;
    setPreviewPhoto(photo);
    setPreviewName(name);
  };

  const fetchLogs = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        visitor: visitorQuery,
        sortBy,
        sortOrder
      });
      if (activeFilters.status && activeFilters.status !== 'All') {
        queryParams.append('status', activeFilters.status);
      }
      if (activeFilters.fromDate) queryParams.append('fromDate', activeFilters.fromDate);
      if (activeFilters.toDate) queryParams.append('toDate', activeFilters.toDate);
      if (activeFilters.hostId) queryParams.append('hostId', activeFilters.hostId);

      const response = await apiFetch(`/api/visits?${queryParams.toString()}`);
      const data = await response.json();
      if (response.ok) {
        setLogs(data.data || []);
        setTotalDocs(data.totalDocs || 0);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLoading(false);
    }
  }, [page, limit, visitorQuery, activeFilters, sortBy, sortOrder]);

  useEffect(() => {
    setPage(1);
  }, [visitorQuery, activeFilters, sortBy, sortOrder]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchLogs();
  }, [fetchLogs]);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const response = await apiFetch(`/api/visits/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, updatedByRole: user.role }),
      });
      if (response.ok) {
        message.success(`Status updated to ${newStatus === 'checked-in' ? 'Checked In' : 'Checked Out'} successfully!`);
        fetchLogs(); // Refresh the list
      } else {
        const errData = await response.json().catch(() => ({}));
        message.error(errData.message || 'Failed to update status.');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Connection error.');
    }
  };

  const escapeCsvValue = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const handleDownloadCsv = () => {
    const rows = filteredLogs.map((log) => [
      log.visitor?.firstName || '',
      log.visitor?.lastName || '',
      log.visitor?.companyName || 'Guest',
      log.host?.firstName || '',
      log.host?.lastName || '',
      log.purpose || '',
      log.checkInTime ? new Date(log.checkInTime).toLocaleString() : '',
      log.checkOutTime ? new Date(log.checkOutTime).toLocaleString() : '',
      log.createdAt ? new Date(log.createdAt).toLocaleDateString() : '',
      log.status || ''
    ]);

    const csv = [
      ['First Name', 'Last Name', 'Company', 'Host First Name', 'Host Last Name', 'Purpose', 'Check In Time', 'Check Out Time', 'Date', 'Status']
        .map(escapeCsvValue)
        .join(','),
      ...rows.map((row) => row.map(escapeCsvValue).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `visitor-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getCleanSearchQuery = (query) => {
    const match = query.match(/(VMS-[A-Z0-9]{6})/i);
    return match ? match[1] : query;
  };

  const filterConfig = [
    {
      key: 'fromDate',
      label: 'From Date',
      type: 'date',
      placeholder: 'Select from date'
    },
    {
      key: 'toDate',
      label: 'To Date',
      type: 'date',
      placeholder: 'Select to date'
    },
    ...(!isEmployee ? [{
      key: 'hostId',
      label: 'Host',
      type: 'select',
      showSearch: true,
      options: employees.map(e => ({ label: `${e.firstName} ${e.lastName}`, value: e._id }))
    }] : []),
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'checked-in', label: 'Checked In' },
        { value: 'checked-out', label: 'Checked Out' },
        { value: 'pending', label: 'Pending' },
        { value: 'pre-approved', label: 'Pre-approved' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'not-visited', label: 'Not-Visited' },
      ]
    }
  ];

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    setPage(1);
    setShowFilterModal(false);
  };

  const handleResetFilters = () => {
    setActiveFilters({});
    setPage(1);
    setShowFilterModal(false);
  };

  const filteredLogs = logs;

  if (userRole !== 'admin' && userRole !== 'receptionist' && !isEmployee) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-8">
        <div className="w-20 h-20 bg-rose-50 dark:bg-rose-900/20 text-rose-500 rounded-full flex items-center justify-center mb-6">
          <FiX className="w-10 h-10 text-rose-500" />
        </div>
        <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">Access Denied</h2>
        <p className="text-slate-500 max-w-sm">You do not have permission to view visitor activity. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div className="wm-page">
      <div className="wm-page-header">
        <div>
          <h1 className="wm-page-title dark:text-white">Visitor Activity</h1>
          <p className="wm-page-subtitle dark:text-slate-400">History of all visitors who accessed the premises.</p>
        </div>
        <Button 
          icon={<FiDownload />}
          size="large"
          onClick={handleDownloadCsv}
          className="wm-btn-secondary h-10"
        >
          Download CSV
        </Button>
      </div>

      {/* Filter Controls Row */}
      <div className="wm-card-header bg-[var(--wm-surface-muted)] dark:bg-slate-800/20 flex flex-col md:flex-row gap-4 items-center justify-between rounded-t-xl border border-slate-200 dark:border-slate-800 border-b-0 mt-4">
        <div className="flex items-center gap-2 max-w-md w-full">
          <Input.Search
            size="large"
            placeholder="Search Visitor Name, Passcode..."
            value={visitorQuery}
            onChange={(e) => setVisitorQuery(e.target.value)}
            onSearch={() => setPage(1)}
            className="w-full custom-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button className="h-10 px-5 font-medium bg-[#e6f4ff] text-[#1677ff] border-transparent hover:bg-[#bae0ff]" onClick={() => setShowFilterModal(true)}>Filter</Button>
          <Button className="h-10 px-5 font-medium bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-700" onClick={handleResetFilters}>Reset</Button>
        </div>
      </div>

      <div className="wm-table-shell dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                <th className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Visitor Details</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Person to Meet & Purpose</th>
                 <th 
                  className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap cursor-pointer select-none hover:bg-slate-200/50 dark:hover:bg-slate-800 transition-colors"
                  onClick={() => {
                    if (sortBy === 'checkInTime') {
                      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                    } else {
                      setSortBy('checkInTime');
                      setSortOrder('desc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    Check In
                    {sortBy === 'checkInTime' && (
                      sortOrder === 'desc' ? <FiArrowDown className="w-3.5 h-3.5 text-slate-500" /> : <FiArrowUp className="w-3.5 h-3.5 text-slate-500" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap cursor-pointer select-none hover:bg-slate-200/50 dark:hover:bg-slate-800 transition-colors"
                  onClick={() => {
                    if (sortBy === 'checkOutTime') {
                      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                    } else {
                      setSortBy('checkOutTime');
                      setSortOrder('desc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    Check Out
                    {sortBy === 'checkOutTime' && (
                      sortOrder === 'desc' ? <FiArrowDown className="w-3.5 h-3.5 text-slate-500" /> : <FiArrowUp className="w-3.5 h-3.5 text-slate-500" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Status</th>
                {canCheckInOut && <th className="px-6 py-4"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
               {loading ? (
                [...Array(5)].map((_, idx) => (
                  <tr key={idx} className="border-b border-slate-200 dark:border-slate-800">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-7 h-7 rounded-lg bg-slate-200 dark:bg-slate-855 animate-pulse mr-3 shrink-0"></div>
                        <div className="space-y-2 flex-1 min-w-[120px]">
                          <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-3/4"></div>
                          <div className="h-2.5 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-1/2"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-28"></div>
                        <div className="h-2.5 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-20"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-16"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-16"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 bg-slate-200 dark:bg-slate-800 rounded-md animate-pulse w-16"></div>
                    </td>
                    {canCheckInOut && (
                      <td className="px-6 py-4 text-right">
                        <div className="h-9 w-20 bg-slate-200 dark:bg-slate-800 rounded-lg animate-pulse ml-auto"></div>
                      </td>
                    )}
                  </tr>
                ))
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={canCheckInOut ? 6 : 5} className="px-8 py-14 text-center text-slate-400 font-medium">No visits found.</td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group cursor-pointer" onClick={() => setSelectedVisitDetails(log)}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {log.visitor?.photo ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPhotoPreview(log.visitor.photo, `${log.visitor?.firstName || 'Unknown'} ${log.visitor?.lastName || 'Visitor'}`);
                            }}
                            className="w-7 h-7 rounded-lg overflow-hidden mr-3 shrink-0 border border-slate-200 dark:border-slate-700"
                            title="View visitor photo"
                          >
                            <img src={log.visitor.photo} alt="Visitor" className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 font-medium text-[10px] mr-3 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/30 group-hover:text-primary-600 transition-all">
                            {log.visitor?.firstName?.charAt(0) || 'V'}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-normal text-slate-800 dark:text-slate-100 truncate">
                            {log.visitor?.firstName} {log.visitor?.lastName}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 font-normal truncate leading-tight">{log.visitor?.companyName || 'Guest'}</div>
                          {log.passCode && (
                            <div className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-medium px-1.5 py-0.5 rounded mt-1 inline-block uppercase tracking-wider">
                              Pass: {log.passCode}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-800 dark:text-slate-100 font-normal truncate">
                        {log.host?.firstName} {log.host?.lastName}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 font-normal leading-tight">{log.purpose}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-800 dark:text-slate-100 font-normal whitespace-nowrap">
                        {formatLogTime(log.checkInTime)}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 font-normal leading-tight whitespace-nowrap">
                        {log.expectedDate
                          ? `Visit: ${new Date(log.expectedDate).toLocaleDateString()}`
                          : `Added: ${new Date(log.createdAt).toLocaleDateString()}`}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-800 dark:text-slate-100 font-normal whitespace-nowrap">
                        {formatLogTime(log.checkOutTime)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-medium normal-case tracking-normal rounded-md ${getVisitStatusBadgeClass(log)}`}>
                        {getVisitStatusLabel(log)}
                      </span>
                    </td>
                    {canCheckInOut && (
                      <td className="px-6 py-4 text-right">
                        {log.status === 'approved' && (
                          <Button 
                            type="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                const visitDateStr = getLocalDateString(log.expectedDate || log.createdAt);
                                if (visitDateStr !== getLocalDateString()) {
                                  message.warning(`Check-in is only allowed on the expected visit date (${visitDateStr})`);
                                  return;
                                }
                                handleStatusUpdate(log._id, 'checked-in');
                            }}
                            className="bg-emerald-500 hover:bg-emerald-600 border-none text-[10px] font-semibold uppercase tracking-widest rounded-lg shadow-lg shadow-emerald-500/20 transition-all h-9"
                          >
                            Check In
                          </Button>
                        )}
                        {log.status === 'checked-in' && (
                          <Button 
                            type="primary"
                            danger
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusUpdate(log._id, 'checked-out');
                            }}
                            className="bg-rose-500 hover:bg-rose-600 border-none text-[10px] font-semibold uppercase tracking-widest rounded-lg shadow-lg shadow-rose-500/20 transition-all h-9"
                          >
                            Check Out
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        <div className="p-4 flex items-center justify-end">
          <Pagination
            current={page}
            pageSize={limit}
            total={totalDocs}
            showSizeChanger
            pageSizeOptions={[10,25,50,100]}
            onChange={(p, ps) => { setPage(ps !== limit ? 1 : p); setLimit(ps); }}
          />
        </div>
      <VisitDetailsModal
        open={Boolean(selectedVisitDetails)}
        onCancel={() => setSelectedVisitDetails(null)}
        visit={selectedVisitDetails}
      />

      <Modal
        open={Boolean(previewPhoto)}
        onCancel={() => setPreviewPhoto('')}
        footer={null}
        title={previewName || 'Visitor Photo'}
        width="min(100%, 32rem)"
        styles={{ body: { padding: '0.75rem 1rem 1rem' } }}
      >
        <div className="flex justify-center">
          <img
            src={previewPhoto}
            alt={previewName || 'Visitor'}
            className="w-full max-h-[70dvh] object-contain rounded-lg border border-slate-200 dark:border-slate-700"
          />
        </div>
      </Modal>
      <FilterModal
        open={showFilterModal}
        onCancel={() => setShowFilterModal(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        config={filterConfig}
        initialValues={activeFilters}
      />
    </div>
  );
};

export default VisitorLogs;
